import React, { useContext, useEffect, useState } from 'react'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { useTranslation } from "react-i18next"
import { BufferService, LoadingContext } from 'src/M_Components'
import { Chip, Box, TextField } from '@mui/material'
import { Search, FilterList } from '@mui/icons-material'
import './ChangeLogPage.css'

export default function ChangeLogPage() {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState('')
    const [expandedFeatures, setExpandedFeatures] = useState({})
    const [dataChangeLog, setDataChangeLog] = useState([])
    const [filteredData, setFilteredData] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [filterType, setFilterType] = useState('all')
    const bufferServiceChangeLog = new BufferService('logChange')
    const ctx = useContext(LoadingContext)

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false)
    }

    const handleFeatureToggle = (featureId) => {
        setExpandedFeatures(prev => ({
            ...prev,
            [featureId]: !prev[featureId]
        }))
    }

    function toJSONLocal(date) {
        return date.slice(0, 10)
    }

    function reformatDate(dateStr) {
        var dArr = dateStr.split("-")  // ex input: "2010-01-18"
        return dArr[2] + "/" + dArr[1] + "/" + dArr[0].substring(0) //ex output: "18/01/2010"
    }

    useEffect(() => {
        loadChangeLogData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadChangeLogData = async () => {
        try {
            ctx.setIsLoad(true)
            // Use the public endpoint that returns only published versions
            const response = await bufferServiceChangeLog.filter('/published')
            const data = Array.isArray(response) ? response : (response.Items || [])
            
            // Already filtered by backend (IsPublished = true), just sort
            const sorted = data.sort((a, b) => 
                new Date(b.ReleaseDate || b.CreatedOn) - new Date(a.ReleaseDate || a.CreatedOn)
            )
            
            setDataChangeLog(sorted)
            setFilteredData(sorted)
            ctx.setIsLoad(false)
        } catch (error) {
            console.error('Error loading changelog:', error)
            if (error.response) {
                ctx.toastMessages('error', '', error.response.data?.message || t('ErrorLoadingData'))
            } else {
                ctx.toastMessages('error', '', t('ErrorLoadingData'))
            }
            ctx.setIsLoad(false)
        }
    }

    useEffect(() => {
        applyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, filterType, dataChangeLog])

    const applyFilters = () => {
        let filtered = [...dataChangeLog]

        // Filter by search term (version or title)
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.Version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.Title?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        // Filter by feature type — keeps all versions but will filter features inside render

        setFilteredData(filtered)
    }

    // Get unique feature types for filter
    const getFeatureTypes = () => {
        const types = new Set()
        dataChangeLog.forEach(log => {
            log.LogFeatureList?.forEach(feature => {
                types.add(feature.Feature)
            })
        })
        return Array.from(types)
    }

    return (
        <>
            {/* Filters Section */}
            <Box className="changelog-filters-container">
                <div className="changelog-filters-wrapper">
                    <div className="changelog-search-field">
                        <TextField
                            fullWidth
                            size="small"
                            label={t('SearchVersionOrTitle')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('SearchPlaceholder')}
                            InputProps={{
                                startAdornment: <Search sx={{ marginRight: '8px', color: '#666' }} />
                            }}
                        />
                    </div>
                    
                    <div className="changelog-filter-field">
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label={t('FilterByType')}
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            SelectProps={{
                                native: true,
                            }}
                        >
                            <option value="all">{t('AllTypes')}</option>
                            {getFeatureTypes().map((type, idx) => (
                                <option key={idx} value={type}>{type}</option>
                            ))}
                        </TextField>
                    </div>

                    <Chip 
                        icon={<FilterList />}
                        label={`${filteredData.length} ${t('Versions')}`}
                        color="primary"
                        variant="outlined"
                    />
                </div>
            </Box>

            <div className='divAccordion'>
                {filteredData.length === 0 ? (
                    <Box className="changelog-empty-state">
                        <p>{searchTerm || filterType !== 'all' ? t('NoVersionsFound') : t('NoPublishedVersions')}</p>
                    </Box>
                ) : (
                    filteredData.filter(value =>
                        filterType === 'all' ||
                        value.LogFeatureList?.some(f => f.Feature?.trim().toLowerCase() === filterType.trim().toLowerCase())
                    ).map((value, index) => {
                        const releaseDate = value.ReleaseDate || value.CreatedOn
                        return (
                            <MuiAccordion disableGutters elevation={1} key={index} square expanded={expanded === `panel ${index}`} onChange={handleChange(`panel ${index}`)} >
                                <MuiAccordionSummary
                                    aria-controls="panel1d-content" id="panel1d-header"
                                    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
                                    sx={{
                                        '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(90deg)', },
                                        '& .MuiAccordionSummary-content': { marginLeft: '20px', },
                                        flexDirection: 'row-reverse',
                                    }}
                                >
                                    <div className="changelog-version-summary-content">
                                        {value.Version && (
                                            <Chip 
                                                label={`v${value.Version}`}
                                                color="primary"
                                                size="small"
                                                sx={{ 
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    height: '24px'
                                                }}
                                            />
                                        )}
                                        <div className="changelog-version-info">
                                            {value.Title && (
                                                <p className="changelog-version-title">
                                                    {value.Title}
                                                </p>
                                            )}
                                            {value.Description && (
                                                <p className="changelog-version-description">
                                                    {value.Description}
                                                </p>
                                            )}
                                            <p className="changelog-version-date">
                                                {t("LabelUpdateDate")}{reformatDate(toJSONLocal(releaseDate))}
                                            </p>
                                        </div>
                                        {value.IsPublished !== undefined && !value.IsPublished && (
                                            <Chip label={t('Draft')} size="small" color="default" />
                                        )}
                                    </div>
                                </MuiAccordionSummary>
                                <MuiAccordionDetails sx={{ borderTop: '1px solid var(--color-grey)', borderBottom: '1px solid var(--color-grey)', padding: '20px', }} >
                                    <div className='mainDiv'>
                                        {value.LogFeatureList && value.LogFeatureList.length > 0 ? (
                                            value.LogFeatureList
                                            .filter(val => filterType === 'all' || val.Feature?.trim().toLowerCase() === filterType.trim().toLowerCase())
                                            .map((val, key) => {
                                                const featureId = `${index}-${key}`
                                                const isExpanded = expandedFeatures[featureId]
                                                const hasDescription = val.Description && val.Description.length > 0
                                                
                                                return (
                                                    <div className="changelog-feature-card" key={key}>
                                                        <div 
                                                            className={`changelog-feature-header ${hasDescription ? 'clickable' : ''}`}
                                                            onClick={() => hasDescription && handleFeatureToggle(featureId)}
                                                            onMouseEnter={(e) => hasDescription && (e.currentTarget.style.backgroundColor = '#e8e8e8')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                                        >
                                                            <div className="changelog-feature-badge-container">
                                                                <Chip
                                                                    label={val.Feature}
                                                                    size="small"
                                                                    sx={{ 
                                                                        backgroundColor: val.Color,
                                                                        color: '#fff',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600,
                                                                        height: '24px',
                                                                        '& .MuiChip-label': {
                                                                            padding: '0 12px'
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="changelog-feature-title-container">
                                                                <span className="changelog-feature-title">
                                                                    {val.Title || val.Description}
                                                                </span>
                                                            </div>
                                                            {hasDescription && (
                                                                <div className="changelog-feature-arrow-container">
                                                                    <ArrowForwardIosSharpIcon 
                                                                        sx={{ 
                                                                            fontSize: '0.9rem',
                                                                            color: '#666',
                                                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                            transition: 'transform 0.3s'
                                                                        }} 
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {hasDescription && isExpanded && (
                                                            <div className="changelog-feature-description">
                                                                {val.Description}
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <p className="changelog-no-features">{t('NoFeaturesAvailable')}</p>
                                        )}
                                    </div>
                                </MuiAccordionDetails>
                            </MuiAccordion>
                        )
                    })
                )}
            </div>
            <div className="changelog-bottom-spacing" />
        </>
    )
}